import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalNavigate as useNavigate, useLocale } from '@/lib/locale';
import { Trans, useTranslation } from 'react-i18next';
import { formatPrice } from '@/lib/catalog';

const DATE_LOCALES = { en: 'en-GB', ru: 'ru-RU', it: 'it-IT' };
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, Package, Mail, Truck } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CheckoutSuccess = ({ onClearCart }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = useLocale();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [orderDetails, setOrderDetails] = useState(null);
  const [shippingData, setShippingData] = useState(null);

  useEffect(() => {
    // Get shipping data from localStorage
    const storedShipping = localStorage.getItem('shipping_data');
    if (storedShipping) {
      setShippingData(JSON.parse(storedShipping));
    }

    const checkPaymentStatus = async () => {
      const sessionId = searchParams.get('session_id');
      if (!sessionId) {
        setStatus('error');
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;

      const poll = async () => {
        try {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`);
          
          if (response.data.payment_status === 'paid') {
            setStatus('success');
            setOrderDetails(response.data);
            onClearCart();
            // Clear shipping data from localStorage
            localStorage.removeItem('shipping_data');
          } else if (attempts >= maxAttempts) {
            setStatus('timeout');
          } else {
            attempts++;
            setTimeout(poll, 2000);
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
          setStatus('error');
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [searchParams, onClearCart]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen pt-10 pb-20 flex items-center justify-center" data-testid="success-page">
        <div className="text-center space-y-4">
          <Loader2 className="w-16 h-16 text-accent mx-auto animate-spin" />
          <p className="text-xl text-foreground">{t('success.confirming')}</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    // Calculate estimated delivery date (3-5 business days from now)
    const estimatedDelivery = new Date();
    estimatedDelivery.setDate(estimatedDelivery.getDate() + 5);
    const deliveryDate = estimatedDelivery.toLocaleDateString(DATE_LOCALES[lang] || 'en-GB', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });

    return (
      <div className="min-h-screen pt-10 pb-20" data-testid="success-page">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto">
            {/* Success Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-accent/10 text-accent mb-6">
                <CheckCircle className="w-16 h-16" data-testid="success-icon" />
              </div>
              
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
                {t('success.title')}
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                {t('success.text')}
              </p>

              {/* Order Number */}
              {orderDetails?.order_id && (
                <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-card border border-border">
                  <span className="text-sm text-muted-foreground">{t('success.orderNumber')}</span>
                  <span className="font-mono font-semibold text-foreground" data-testid="order-id">
                    #{orderDetails.order_id.substring(0, 8).toUpperCase()}
                  </span>
                </div>
              )}
            </div>

            {/* Order Details Cards */}
            <div className="space-y-6">
              {/* Confirmation Email Notice */}
              <div className="p-6 rounded-2xl bg-accent/10 border border-accent/30">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{t('success.emailTitle')}</h3>
                    <p className="text-sm text-muted-foreground">
                      <Trans
                        i18nKey="success.emailText"
                        values={{ email: shippingData?.email || '' }}
                        components={{ 1: <span className="text-foreground font-medium" /> }}
                      />
                    </p>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              {shippingData && (
                <div className="p-8 rounded-2xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <Truck className="w-6 h-6 text-accent" />
                    <h2 className="text-xl font-heading font-semibold text-foreground">{t('success.shippingAddress')}</h2>
                  </div>
                  <div className="space-y-1 text-muted-foreground">
                    <p className="text-foreground font-semibold text-lg">
                      {shippingData.firstName} {shippingData.lastName}
                    </p>
                    <p>{shippingData.streetAddress}</p>
                    {shippingData.apartment && <p>{shippingData.apartment}</p>}
                    <p>{shippingData.city}, {shippingData.postalCode}</p>
                    <p>{shippingData.country}</p>
                    <div className="pt-4 border-t border-border mt-4">
                      <p>{shippingData.email}</p>
                      <p>{shippingData.phone}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Estimated Delivery */}
              <div className="p-8 rounded-2xl bg-card border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <Package className="w-6 h-6 text-accent" />
                  <h2 className="text-xl font-heading font-semibold text-foreground">{t('success.estimatedDelivery')}</h2>
                </div>
                <p className="text-2xl font-bold text-accent mb-2">{deliveryDate}</p>
                <p className="text-sm text-muted-foreground">
                  {t('success.deliveryText')}
                </p>
              </div>

              {/* Discreet Packaging Notice */}
              <div className="p-6 rounded-2xl bg-secondary border border-border">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                    <Package className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {t('success.discreetTitle')}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t('success.discreetText')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="p-8 rounded-2xl bg-card border border-border">
                <h2 className="text-xl font-heading font-semibold text-foreground mb-6">{t('success.orderSummary')}</h2>
                <div className="space-y-4">
                  {/* This would ideally come from the order data, but since we clear cart, we show total */}
                  <div className="flex justify-between items-center text-lg pt-4 border-t border-border">
                    <span className="font-semibold text-foreground">{t('success.totalPaid')}</span>
                    <span className="font-bold text-accent text-2xl">
                      {formatPrice((orderDetails?.amount_total || 0) / 100)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-12 space-y-4">
              <Button
                data-testid="continue-shopping-btn"
                onClick={() => navigate('/shop')}
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold py-6 text-lg rounded-xl"
              >
                {t('success.continueShopping')}
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full border-2 border-border text-foreground hover:bg-muted py-6 text-lg rounded-xl"
              >
                {t('success.backHome')}
              </Button>
            </div>

            {/* Support Note */}
            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground">
                {t('success.help')}{' '}
                <a href="mailto:support@blissful-play.com" className="text-accent hover:text-accent/90">
                  support@blissful-play.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-10 pb-20 flex items-center justify-center">
      <div className="max-w-2xl mx-auto px-6 text-center space-y-8">
        <h1 className="text-4xl font-heading font-bold text-foreground">{t('success.statusTitle')}</h1>
        <p className="text-lg text-muted-foreground">
          {status === 'error' ? t('success.statusError') : t('success.statusTimeout')}
        </p>
        <Button onClick={() => navigate('/shop')}>{t('success.backToShop')}</Button>
      </div>
    </div>
  );
};
